import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { MoneySafe } from '../../types';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';

interface SafeFormProps {
  safe?: MoneySafe;
  onSubmit: (data: Partial<MoneySafe>) => void;
  onCancel: () => void;
}

const SafeForm: React.FC<SafeFormProps> = ({ safe, onSubmit, onCancel }) => {
  // Fetch currencies from API
  const { data: currencies } = useCurrencies();

  // Saved currency may be a populated object, a documentId, or a legacy code
  // like "USD"; the select option values are documentIds
  const savedCurrencyId = safe ? resolveCurrencyDocumentId(safe.currency, currencies) : '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<MoneySafe>({
    defaultValues: safe ? {
      ...safe,
      currency: savedCurrencyId as any
    } : {
      name: '',
      location: '',
      currency: '' as any,
      balance: 0,
      responsiblePerson: '',
      isActive: true
    }
  });

  // Re-apply the saved currency once the async options are loaded (edit case)
  useEffect(() => {
    if (savedCurrencyId && currencies && currencies.length > 0) {
      setValue('currency', savedCurrencyId as any);
    }
  }, [savedCurrencyId, currencies, setValue]);

  const handleFormSubmit = (data: MoneySafe) => {
    onSubmit({
      ...data,
      ...(safe ? { id: safe.id, documentId: safe.documentId } : {}),
      createdAt: safe?.createdAt || new Date().toISOString()
    });
  };

  // Currency options from API - NO HARDCODED VALUES
  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) {
      return [];
    }
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`
      }));
  }, [currencies]);

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {safe ? 'Edit Safe Box' : 'Add New Safe Box'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Safe Box Name"
            {...register('name', {
              required: 'Safe box name is required',
              minLength: { value: 2, message: 'Safe box name must be at least 2 characters' }
            })}
            error={errors.name?.message}
            placeholder="e.g., Main Office Safe"
          />

          <Input
            label="Location"
            {...register('location', {
              required: 'Location is required',
              minLength: { value: 2, message: 'Location must be at least 2 characters' }
            })}
            error={errors.location?.message}
            placeholder="e.g., New York Office"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Currency"
            {...register('currency', { required: 'Currency is required' })}
            options={currencyOptions}
            placeholder="Select currency"
            error={errors.currency?.message}
          />

          <Input
            label="Responsible Person"
            {...register('responsiblePerson', {
              required: 'Responsible person is required',
              minLength: { value: 2, message: 'Responsible person name must be at least 2 characters' }
            })}
            error={errors.responsiblePerson?.message}
            placeholder="e.g., John Smith"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Initial Balance"
            type="number"
            step="0.01"
            {...register('balance', { 
              required: 'Balance is required',
              min: { value: 0, message: 'Balance cannot be negative' }
            })}
            error={errors.balance?.message}
            placeholder="0.00"
          />

          <Select
            label="Status"
            {...register('isActive', { 
              required: 'Status is required',
              setValueAs: (value: string) => value === 'true'
            })}
            options={statusOptions}
            error={errors.isActive?.message}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {safe ? 'Update Safe Box' : 'Create Safe Box'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SafeForm;
